import { useEffect, useState } from "react";
import { CheckCircle2, ChevronLeft, ChevronRight, KeyRound, Loader2, Search, XCircle } from "lucide-react";
import { ErrorState } from "#/components/common/ErrorState";
import { PageLoader } from "#/components/common/PageLoader";
import { usePermissions, useTogglePermission } from "#/features/permissions/hooks";
import type { PermissionResponse } from "#/features/permissions/types";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/** Debounces a fast-changing value — so the permissions search doesn't fire a new
 * request (and, without this, swap the whole page for a loader) on every keystroke. */
function useDebouncedValue<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export default function PermissionPage() {
  // The input is bound to `searchInput` so every keystroke lands immediately and the
  // field never loses focus; the actual query only re-runs 350ms after typing stops,
  // against `debouncedSearch`.
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 350);
  const [filter, setFilter] = useState<"ALL" | "active" | "inactive">("ALL");
  const [toggling, setToggling] = useState<number | null>(null);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const { data, isLoading, isFetching, isError, error } = usePermissions(debouncedSearch);
  const toggle = useTogglePermission();

  const permissions = data ?? [];
  const activeCount   = permissions.filter((p) => p.active).length;
  const inactiveCount = permissions.length - activeCount;

  const filtered = permissions.filter((p) => {
    if (filter === "active")   return p.active;
    if (filter === "inactive") return !p.active;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages - 1);
  const paged      = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  function handleFilterChange(f: typeof filter) {
    setFilter(f);
    setPage(0);
  }

  function handleSearchChange(v: string) {
    setSearchInput(v);
    setPage(0);
  }

  async function handleToggle(perm: PermissionResponse) {
    const next = !perm.active;
    setToggling(perm.id);
    try {
      await toggle.mutateAsync({ id: perm.id, active: next });
      setToastMsg(`${perm.code} ${next ? "activated" : "deactivated"}`);
      setTimeout(() => setToastMsg(null), 3000);
    } catch {
      setToastMsg(`Failed to update ${perm.code}`);
      setTimeout(() => setToastMsg(null), 3000);
    } finally {
      setToggling(null);
    }
  }

  if (isLoading && !data) return <PageLoader label="Loading permissions…" />;
  if (isError)   return <ErrorState title="Failed to load permissions" message={error?.message} />;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Permissions</h1>
        </div>
      </div>

      {/* Stats chips */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: "Total",    value: permissions.length, cls: "bg-white border-slate-200 text-slate-700",      flt: "ALL"      as const },
          { label: "Active",   value: activeCount,        cls: "bg-emerald-50 border-emerald-200 text-emerald-800", flt: "active"   as const },
          { label: "Inactive", value: inactiveCount,      cls: "bg-slate-100 border-slate-200 text-slate-600",  flt: "inactive" as const },
        ].map((s) => (
          <button
            key={s.label}
            type="button"
            onClick={() => handleFilterChange(s.flt)}
            className={`rounded-xl border px-3 py-1.5 text-xs font-bold transition-all ${s.cls} ${filter === s.flt ? "ring-2 ring-emerald-400" : "hover:opacity-80"}`}
          >
            {s.label}: {s.value}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="grid gap-2 rounded-2xl border bg-white p-3 shadow-sm sm:grid-cols-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by code, name, or description…"
            className="w-full rounded-xl border py-2 pl-9 pr-8 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          />
          {isFetching && (
            <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />
          )}
        </div>
        <select value={filter} onChange={(e) => handleFilterChange(e.target.value as typeof filter)}
          className="rounded-xl border px-3 py-2 text-sm outline-none focus:border-emerald-500">
          <option value="ALL">All permissions</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      {/* Toast */}
      {toastMsg && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700">
          {toastMsg}
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="rounded-2xl border bg-white p-10 text-center shadow-sm">
          <KeyRound className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-500">No permissions match your filters.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="border-b bg-slate-50">
                <tr className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5 text-left">Permission</th>
                  <th className="px-4 py-2.5 text-left">Name</th>
                  <th className="px-4 py-2.5 text-left">Description</th>
                  <th className="px-4 py-2.5 text-center">Status</th>
                  <th className="px-4 py-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((perm) => (
                  <PermissionRow
                    key={perm.id}
                    perm={perm}
                    toggling={toggling === perm.id}
                    onToggle={() => handleToggle(perm)}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-slate-50 px-4 py-2.5">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>
                {filtered.length === 0 ? "0" : `${safePage * pageSize + 1}–${Math.min(safePage * pageSize + pageSize, filtered.length)}`}
                {" "}of {filtered.length.toLocaleString()} permission{filtered.length !== 1 ? "s" : ""}
              </span>
              <span className="text-slate-300">|</span>
              <label className="flex items-center gap-1">
                Per page:
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  className="rounded border px-1.5 py-0.5 text-xs outline-none focus:border-emerald-500"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(0)}
                disabled={safePage === 0}
                className="rounded-lg border px-2 py-1 text-xs disabled:opacity-40 hover:bg-slate-100"
              >
                «
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                className="rounded-lg border px-2 py-1 text-xs disabled:opacity-40 hover:bg-slate-100"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              {/* Page number pills */}
              {Array.from({ length: totalPages }, (_, i) => i)
                .filter((i) => Math.abs(i - safePage) <= 2)
                .map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setPage(i)}
                    className={`min-w-[28px] rounded-lg border px-2 py-1 text-xs font-semibold ${
                      i === safePage
                        ? "border-emerald-500 bg-emerald-600 text-white"
                        : "hover:bg-slate-100"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                className="rounded-lg border px-2 py-1 text-xs disabled:opacity-40 hover:bg-slate-100"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages - 1)}
                disabled={safePage >= totalPages - 1}
                className="rounded-lg border px-2 py-1 text-xs disabled:opacity-40 hover:bg-slate-100"
              >
                »
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function PermissionRow({
  perm,
  toggling,
  onToggle,
}: {
  perm: PermissionResponse;
  toggling: boolean;
  onToggle: () => void;
}) {
  return (
    <tr className="border-t text-sm hover:bg-slate-50/60">

      {/* Code */}
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-3.5 w-3.5 shrink-0 text-indigo-400" />
          <span className="font-mono text-xs font-bold text-slate-800">{perm.code}</span>
        </div>
      </td>

      {/* Name */}
      <td className="px-4 py-2">
        <span className="font-medium text-slate-700">{perm.name}</span>
      </td>

      {/* Description */}
      <td className="px-4 py-2 max-w-xs">
        <span className="text-xs text-slate-500">{perm.description || "—"}</span>
      </td>

      {/* Status badge */}
      <td className="px-4 py-2 text-center">
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${perm.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          {perm.active
            ? <><CheckCircle2 className="h-3 w-3" /> Active</>
            : <><XCircle className="h-3 w-3" /> Inactive</>}
        </span>
      </td>

      {/* Toggle action */}
      <td className="px-4 py-2 text-right">
        <button
          type="button"
          onClick={onToggle}
          disabled={toggling}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            perm.active
              ? "border border-red-200 text-red-600 hover:bg-red-50"
              : "border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          }`}
        >
          {toggling ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : perm.active ? (
            <><XCircle className="h-3 w-3" /> Deactivate</>
          ) : (
            <><CheckCircle2 className="h-3 w-3" /> Activate</>
          )}
        </button>
      </td>
    </tr>
  );
}
